import get from 'lodash/get';
import {
  gettingLanguageNames,
  getItemOrDefaultToValue,
  transformArrayDisplay,
  nestedDisplay,
} from '../../details';

import { columns as qualificationColumn } from 'utils/columns/qualifications';
import { displayTime, formatDuration } from 'utils/time';
import {
  getDisplayRoles,
  getRoleId,
  getUserStatusId,
} from 'utils/helper/roles';
import { ROLES } from 'utils/constants/roles';
import { DEFAULT_DATE_FORMAT, DEFAULT_EMPTY_VALUE } from 'utils/constants';
import { hasFeature } from 'utils/helper/product';
import { shiftColumn } from 'containers/Pages/People/Attendance/Layouts/option';
import { setSiteName } from 'utils/helper/site';
import {
  translateActivityType,
  reorderAccordingRecentActivity,
} from 'containers/Pages/People/Attendance/Layouts/attendanceTrackingHelper';
import { getAssetCategoryData } from 'utils/helper/assetType';
import { translateValue } from 'utils/methods/helpers';

export const checkForSpecificRole = (allRoles, specificRoles) => {
  return specificRoles.some(selectedrole => allRoles.includes(selectedrole));
};

export const transformPersonnelDetails = ({
  data,
  languageList,
  timeZone,
  subscribedFeatures,
  shiftDetails,
  globalUserRecord,
  globalDepartmentList = [],
}) => {
  const selectedDepartments = globalDepartmentList.filter(f =>
    getItemOrDefaultToValue(data.general, 'departmentIds', []).includes(f.id),
  );

  const assignedSite = getItemOrDefaultToValue(data.details, 'assignedSite');
  const roles = getItemOrDefaultToValue(data.general, 'roles', []);
  const reorderedShiftActivities = shiftDetails.activities
    ? reorderAccordingRecentActivity(shiftDetails.activities)
    : [];
  const hasAttendanceTrackingFeature = hasFeature({
    subscribedFeatures,
    feature: 'ATTENDANCE_TRACKING',
  });
  return {
    generalInfo: {
      employeeId: {
        type: 'text',
        label: 'Employee ID',
        value: getItemOrDefaultToValue(data.general, 'employeeId', '-'),
      },
      role: {
        type: 'text',
        value: getDisplayRoles(data.general),
        idValue: getRoleId(data.general),
      },
      firstName: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'firstName', '-'),
      },
      lastName: {
        type: 'text',
        value: getItemOrDefaultToValue(data.general, 'lastName', '-'),
      },
      //show email when role has responder/supervisor/manager/driver
      ...(checkForSpecificRole(roles, [
        ROLES.RESPONDER.id,
        ROLES.SUPERVISOR.id,
        ROLES.MANAGER.id,
        ROLES.DRIVER.id,
      ]) && {
        email: {
          type: 'text',
          value: getItemOrDefaultToValue(data.general, 'email', '-'),
        },
      }),
      phone: {
        type: 'text',
        value:
          data.general.phoneNumber !== null
            ? `${getItemOrDefaultToValue(
                data.general.phoneNumber,
                'prefix',
              )} ${getItemOrDefaultToValue(
                data.general.phoneNumber,
                'number',
                '-',
              )}`
            : '-',
      },
      //all user can now be assigned to a site
      assignedSite: {
        type: 'text',
        value:
          data.details.assignedSite.siteName !== null &&
          data.details.assignedSite.siteName !== ''
            ? setSiteName(assignedSite)
            : '-',
      },
      ...(checkForSpecificRole(roles, [
        ROLES.GUARD.id,
        ROLES.CLEANER.id,
        ROLES.MAINTAINER.id,
      ]) && {
        //show team lead too
        teamLead: {
          type: 'boolean',
          value: data.general.isTeamLead,
        },
      }),
      departments: {
        type: 'array',
        value: nestedDisplay(selectedDepartments),
      },
    },
    detailedInfo: {
      services: {
        type: 'icons',
        value: getItemOrDefaultToValue(data.details, 'services', []),
      },
      qualifications: {
        type: 'list-data',
        value: {
          data:
            data.details.qualifications != null
              ? getItemOrDefaultToValue(data.details, 'qualifications', []).map(
                  qualification => {
                    return {
                      ...qualification,
                      category: getAssetCategoryData(qualification.category),
                      validFrom: displayTime({
                        timestamp: qualification.validFrom,
                        format: DEFAULT_DATE_FORMAT,
                        timeZone,
                      }),
                      validTo: qualification.validTo
                        ? displayTime({
                            timestamp: qualification.validTo,
                            format: DEFAULT_DATE_FORMAT,
                            timeZone,
                          })
                        : '-',
                    };
                  },
                )
              : [],
          columns: qualificationColumn,
        },
      },
      //show if the user have subscribed to Attendance Tracking feature
      ...(hasAttendanceTrackingFeature && {
        shiftBreakdown: {
          type: 'list-data',
          label: 'Shift Breakdown',
          value: {
            data:
              reorderedShiftActivities.length > 0
                ? reorderedShiftActivities.map(item => ({
                    activityType:
                      translateActivityType(item.activityType) ||
                      DEFAULT_EMPTY_VALUE,
                    activityTime: item.activityTime
                      ? displayTime({ timestamp: item.activityTime })
                      : DEFAULT_EMPTY_VALUE,
                    reporter: item.createdById
                      ? globalUserRecord[item.createdById]
                      : DEFAULT_EMPTY_VALUE,
                    duration: item.duration
                      ? formatDuration(item.duration)
                      : DEFAULT_EMPTY_VALUE,
                  }))
                : [],
            columns: shiftColumn,
          },
        },
      }),
      notes: {
        type: 'textArea',
        value: getItemOrDefaultToValue(data.details, 'notes', '-'),
      },
      languages: {
        type: 'text',
        value:
          data.details.languages != null
            ? gettingLanguageNames(
                get(data.details, 'languages', []),
                languageList,
              )
            : '-',
      },
    },
    others: {
      id: { value: data.general.id },
      assignedSite: {
        // assignedSite is an async component with search key as name, so it should be id and name object
        value: {
          id: assignedSite.id,
          name: setSiteName(assignedSite),
        },
      },
      departments: {
        value: selectedDepartments,
      },
      notes: {
        value: getItemOrDefaultToValue(data.details, 'notes', ''),
      },
      phone: {
        value:
          data.general.phoneNumber !== null
            ? getItemOrDefaultToValue(data.general.phoneNumber, 'number', '-')
            : '-',
      },
      image: {
        value: getItemOrDefaultToValue(data.general, 'photoUrl'),
      },
      archived: {
        value: getItemOrDefaultToValue(data.details, 'archived', false),
      },
      availabilityStatus: {
        value: translateValue(data.operational.status), // this is required for displaying status value in detail panel
        idValue: getUserStatusId(data.operational), // this requires enum constants for dispalying correct icons
      },
      languages: {
        value: getItemOrDefaultToValue(data.details, 'languages', []),
      },
      country: {
        value:
          data.general.phoneNumber !== null
            ? getItemOrDefaultToValue(data.general.phoneNumber, 'country', '-')
            : '-',
      },
      experiences: {
        value: getItemOrDefaultToValue(data.details, 'experiences', []),
      },
      roles: {
        value: roles,
      },
      qualifications: {
        type: 'array',
        value:
          data.details.qualifications != null
            ? transformArrayDisplay(
                getItemOrDefaultToValue(data.details, 'qualifications', []),
              )
            : [],
      },
      isResponder: {
        value: roles.includes(ROLES.RESPONDER.id),
      },
      assignedAssets: {
        value: getItemOrDefaultToValue(data.operational, 'assignedAssets', []),
      },
      isTeamLead: {
        value: data.general.isTeamLead,
      },
    },
  };
};
